/**
 * Error Types and Messages Constants
 * Centralized error handling for the OFC application
 */

// Error Type Enums
export const ERROR_TYPES = {
  // Network Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',

  // Server Errors
  SERVER_ERROR: 'SERVER_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Authentication Errors
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  ACCESS_REVOKED: 'ACCESS_REVOKED',
  INVALID_TOKEN: 'INVALID_TOKEN',

  // Account Errors
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  AWAITING_VERIFICATION: 'AWAITING_VERIFICATION',
  KYC_PENDING: 'KYC_PENDING',
  KYC_REJECTED: 'KYC_REJECTED',

  // Rate Limiting
  RATE_LIMITED: 'RATE_LIMITED',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',

  // Validation Errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',

  // Resource Errors
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_DELETED: 'RESOURCE_DELETED',
  RESOURCE_UNAVAILABLE: 'RESOURCE_UNAVAILABLE',

  // Permission Errors
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',

  // Generic
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

// HTTP Status to Error Type mapping
export const HTTP_STATUS_ERROR_MAP = {
  400: ERROR_TYPES.VALIDATION_ERROR,
  401: ERROR_TYPES.UNAUTHORIZED,
  403: ERROR_TYPES.FORBIDDEN,
  404: ERROR_TYPES.NOT_FOUND,
  408: ERROR_TYPES.NETWORK_TIMEOUT,
  429: ERROR_TYPES.TOO_MANY_REQUESTS,
  500: ERROR_TYPES.SERVER_ERROR,
  502: ERROR_TYPES.SERVICE_UNAVAILABLE,
  503: ERROR_TYPES.SERVICE_UNAVAILABLE,
  504: ERROR_TYPES.NETWORK_TIMEOUT,
};

// Error Messages
export const ERROR_MESSAGES = {
  // Network Errors
  [ERROR_TYPES.NETWORK_ERROR]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    action: 'Retry',
    recoverable: true,
  },
  [ERROR_TYPES.NETWORK_TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long to complete. Please try again.',
    action: 'Retry',
    recoverable: true,
  },
  [ERROR_TYPES.NETWORK_OFFLINE]: {
    title: "You're Offline",
    message: 'No internet connection detected. Please check your network settings.',
    action: 'Retry',
    recoverable: true,
  },

  // Server Errors
  [ERROR_TYPES.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Our team has been notified.',
    action: 'Retry',
    recoverable: true,
  },
  [ERROR_TYPES.DATABASE_ERROR]: {
    title: 'Data Error',
    message: 'There was a problem accessing your data. Please try again.',
    action: 'Retry',
    recoverable: true,
  },
  [ERROR_TYPES.SERVICE_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable. Please try again in a few minutes.',
    action: 'Retry Later',
    recoverable: true,
  },

  // Authentication Errors
  [ERROR_TYPES.SESSION_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again to continue.',
    action: 'Login',
    recoverable: true,
    redirectTo: '/login',
  },
  [ERROR_TYPES.UNAUTHORIZED]: {
    title: 'Authentication Required',
    message: 'Please log in to access this feature.',
    action: 'Login',
    recoverable: true,
    redirectTo: '/login',
  },
  [ERROR_TYPES.ACCESS_REVOKED]: {
    title: 'Access Revoked',
    message: 'Your access to this resource has been revoked. Please contact support.',
    action: 'Contact Support',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.INVALID_TOKEN]: {
    title: 'Invalid Session',
    message: 'Your session is invalid. Please log in again.',
    action: 'Login',
    recoverable: true,
    redirectTo: '/login',
  },

  // Account Errors
  [ERROR_TYPES.ACCOUNT_SUSPENDED]: {
    title: 'Account Suspended',
    message: 'Your account has been suspended. Please contact support for assistance.',
    action: 'Contact Support',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.ACCOUNT_LOCKED]: {
    title: 'Account Locked',
    message: 'Your account has been temporarily locked due to security reasons.',
    action: 'Contact Support',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.ACCOUNT_DELETED]: {
    title: 'Account Not Found',
    message: 'This account no longer exists.',
    action: 'Create Account',
    recoverable: false,
    redirectTo: '/register',
  },
  [ERROR_TYPES.AWAITING_VERIFICATION]: {
    title: 'Verification Pending',
    message: "Your account is awaiting verification. You'll receive a notification once verified.",
    action: 'Check Status',
    recoverable: true,
  },
  [ERROR_TYPES.KYC_PENDING]: {
    title: 'KYC Pending',
    message: 'Your KYC verification is still in progress. This usually takes 24-48 hours.',
    action: 'View Status',
    recoverable: true,
  },
  [ERROR_TYPES.KYC_REJECTED]: {
    title: 'KYC Rejected',
    message: 'Your KYC documents were rejected. Please re-submit valid documents.',
    action: 'Re-submit Documents',
    recoverable: true,
    redirectTo: '/profile',
  },

  // Rate Limiting
  [ERROR_TYPES.RATE_LIMITED]: {
    title: 'Slow Down',
    message: "You're making too many requests. Please wait a moment.",
    action: 'Wait',
    recoverable: true,
  },
  [ERROR_TYPES.TOO_MANY_REQUESTS]: {
    title: 'Too Many Requests',
    message: 'Request limit exceeded. Please try again in a few minutes.',
    action: 'Retry Later',
    recoverable: true,
  },

  // Validation Errors
  [ERROR_TYPES.VALIDATION_ERROR]: {
    title: 'Invalid Data',
    message: 'Please check your input and try again.',
    action: 'Fix & Retry',
    recoverable: true,
  },
  [ERROR_TYPES.INVALID_INPUT]: {
    title: 'Invalid Input',
    message: 'The information you provided is not valid.',
    action: 'Fix',
    recoverable: true,
  },
  [ERROR_TYPES.MISSING_REQUIRED_FIELD]: {
    title: 'Missing Information',
    message: 'Please fill in all required fields.',
    action: 'Complete Form',
    recoverable: true,
  },

  // Resource Errors
  [ERROR_TYPES.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'Go Back',
    recoverable: true,
  },
  [ERROR_TYPES.RESOURCE_DELETED]: {
    title: 'Resource Deleted',
    message: 'This item has been deleted and is no longer available.',
    action: 'Go Back',
    recoverable: false,
  },
  [ERROR_TYPES.RESOURCE_UNAVAILABLE]: {
    title: 'Temporarily Unavailable',
    message: 'This resource is temporarily unavailable. Please try again later.',
    action: 'Retry',
    recoverable: true,
  },

  // Permission Errors
  [ERROR_TYPES.FORBIDDEN]: {
    title: 'Access Denied',
    message: "You don't have permission to access this resource.",
    action: 'Go Back',
    recoverable: false,
  },
  [ERROR_TYPES.INSUFFICIENT_PERMISSIONS]: {
    title: 'Insufficient Permissions',
    message: "Your account doesn't have the required permissions for this action.",
    action: 'Contact Admin',
    recoverable: false,
  },

  // Generic
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    action: 'Retry',
    recoverable: true,
  },
};

/**
 * Get error type from HTTP status code
 * @param {number} statusCode - HTTP status code
 * @returns {string} Error type
 */
export function getErrorTypeFromStatus(statusCode) {
  return HTTP_STATUS_ERROR_MAP[statusCode] || ERROR_TYPES.UNKNOWN_ERROR;
}

/**
 * Get error config from error type
 * @param {string} errorType - Error type from ERROR_TYPES
 * @returns {Object} Error configuration
 */
export function getErrorConfig(errorType) {
  return ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
}

/**
 * Parse API error response and return standardized error object
 * @param {Error|Object} error - Error object from API call
 * @returns {Object} Standardized error object
 */
export function parseApiError(error) {
  // Handle network errors
  if (!navigator.onLine) {
    return {
      type: ERROR_TYPES.NETWORK_OFFLINE,
      ...ERROR_MESSAGES[ERROR_TYPES.NETWORK_OFFLINE],
    };
  }

  if (error?.name === 'TypeError' || error?.message?.includes('fetch')) {
    return {
      type: ERROR_TYPES.NETWORK_ERROR,
      ...ERROR_MESSAGES[ERROR_TYPES.NETWORK_ERROR],
    };
  }

  // Handle HTTP status codes
  const status = error?.status || error?.response?.status;
  if (status) {
    // Special handling for specific error messages from backend
    // Prefer backend-provided message when available (response.data.message or response.data.error.message)
    const errorMessage =
      error?.response?.data?.message || error?.response?.data?.error?.message || error?.message;

    if (status === 401) {
      if (errorMessage?.toLowerCase().includes('expired')) {
        return {
          type: ERROR_TYPES.SESSION_EXPIRED,
          ...ERROR_MESSAGES[ERROR_TYPES.SESSION_EXPIRED],
          originalMessage: errorMessage,
        };
      }
      if (errorMessage?.toLowerCase().includes('revoked')) {
        return {
          type: ERROR_TYPES.ACCESS_REVOKED,
          ...ERROR_MESSAGES[ERROR_TYPES.ACCESS_REVOKED],
          originalMessage: errorMessage,
        };
      }
    }

    if (status === 403) {
      if (errorMessage?.toLowerCase().includes('suspended')) {
        return {
          type: ERROR_TYPES.ACCOUNT_SUSPENDED,
          ...ERROR_MESSAGES[ERROR_TYPES.ACCOUNT_SUSPENDED],
          originalMessage: errorMessage,
        };
      }
      if (errorMessage?.toLowerCase().includes('verification')) {
        return {
          type: ERROR_TYPES.AWAITING_VERIFICATION,
          ...ERROR_MESSAGES[ERROR_TYPES.AWAITING_VERIFICATION],
          originalMessage: errorMessage,
        };
      }
    }

    const errorType = getErrorTypeFromStatus(status);
    return {
      type: errorType,
      ...ERROR_MESSAGES[errorType],
      originalMessage: errorMessage,
    };
  }

  // Default to unknown error
  return {
    type: ERROR_TYPES.UNKNOWN_ERROR,
    ...ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR],
    originalMessage: error?.message,
  };
}

export default ERROR_TYPES;
